<template>
    <ui-modal
        :is-open="show"
        @close="closeModal"
        :max-width="{ 'max-width': '700px' }"
    >
        <template #title>
            <header class="h-[52px] flex items-center gap-3">
                <div class="flex-1 pr-12">
                    <div class="flex justify-between items-center mb-1">
                        <h1
                            class="text-slate-800 dark:text-slate-100 text-xl font-medium font-['Inter'] leading-7"
                        >
                            Copy card details to another card
                        </h1>
                        <div class="w-5 h-5 p-2 rounded overflow-hidden"></div>
                    </div>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal font-['Inter'] leading-tight"
                    >
                        {{ subheaderText }}
                    </p>
                </div>
            </header>
        </template>

        <template #content>
            <!-- Steps 1 & 2: Card selection + Detail picking -->
            <CopyCardSelector
                v-if="currentStep === 1 || currentStep === 2"
                :selectedCard="selectedCard"
                :selectedReceivingCard="selectedReceivingCard"
                :currentStep="currentStep"
                :errors="errors"
                @set-card-to-copy="setCardToCopy"
                @set-receiving-card="setReceivingCard"
            >
                <template #source-details>
                    <TransitionGroup name="fade">
                        <CopyDetailsPicker
                            v-if="currentStep === 2 && selectedCard"
                            key="details"
                            :currentStep="currentStep"
                            :selectedCard="selectedCard"
                            :selectedDetails="selectedDetails"
                            :selectAll="selectAll"
                            :errors="errors"
                            :copyableDetails="copyableDetails"
                            :welcomeBonusDetails="welcomeBonusDetails"
                            :productChangedDetails="productChangedDetails"
                            :formatCardDetails="formatCardDetails"
                            :formatBonusOfferDisplay="formatBonusOfferDisplay"
                            @toggle-detail="handleToggleDetail"
                            @toggle-select-all="handleToggleSelectAll"
                        />
                    </TransitionGroup>
                </template>
                <template #receiving-details>
                    <TransitionGroup name="fade">
                        <CopyDetailsResult
                            v-if="currentStep === 2"
                            key="receiving-step2"
                            :currentStep="currentStep"
                            :selectedCard="selectedCard"
                            :selectedReceivingCard="selectedReceivingCard"
                            :selectedDetails="selectedDetails"
                            :copyableDetails="copyableDetails"
                            :welcomeBonusDetails="welcomeBonusDetails"
                            :productChangedDetails="productChangedDetails"
                            :isSelected="isSelected"
                            :formatCardDetails="formatCardDetails"
                            :formatBonusOfferDisplay="formatBonusOfferDisplay"
                        />
                    </TransitionGroup>
                </template>
            </CopyCardSelector>

            <!-- Step 3: Confirmation -->
            <CopyConfirmation
                :currentStep="currentStep"
                :selectedCard="selectedCard"
                :selectedReceivingCard="selectedReceivingCard"
                :selectedDetails="selectedDetails"
                :confirmChanges="confirmChanges"
                :errors="errors"
                :copyableDetails="copyableDetails"
                :welcomeBonusDetails="welcomeBonusDetails"
                :productChangedDetails="productChangedDetails"
                :isSelected="isSelected"
                :formatCardDetails="formatCardDetails"
                :formatBonusOfferDisplay="formatBonusOfferDisplay"
                @update:confirmChanges="confirmChanges = $event"
            />
        </template>

        <template #footer>
            <CopyCardFooter
                :currentStep="currentStep"
                @next="nextStep"
                @prev="prevStepOrCancel"
            />
        </template>
    </ui-modal>
</template>

<script setup>
import UiModal from '@/components/ui/modal/ui-modal.vue';
import { useCopyCardWizard } from '@/composables/useCopyCardWizard';
import { useCardDetailFormatter } from '@/composables/useCardDetailFormatter';
import CopyCardSelector from './copy-card-details/CopyCardSelector.vue';
import CopyDetailsPicker from './copy-card-details/CopyDetailsPicker.vue';
import CopyDetailsResult from './copy-card-details/CopyDetailsResult.vue';
import CopyConfirmation from './copy-card-details/CopyConfirmation.vue';
import CopyCardFooter from './copy-card-details/CopyCardFooter.vue';

const props = defineProps({
    show: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { formatCardDetails, formatBonusOfferDisplay } = useCardDetailFormatter();

const {
    selectedCard,
    currentStep,
    selectedReceivingCard,
    selectAll,
    confirmChanges,
    errors,
    selectedDetails,
    subheaderText,
    copyableDetails,
    welcomeBonusDetails,
    productChangedDetails,
    isSelected,
    nextStep,
    setReceivingCard,
    setCardToCopy,
    prevStepOrCancel,
    closeModal,
    toggleSelectAll,
    onDetailToggled,
} = useCopyCardWizard(emit);

const handleToggleDetail = (key, checked) => {
    selectedDetails.value[key] = checked;
    onDetailToggled();
};

const handleToggleSelectAll = (checked) => {
    selectAll.value = checked;
    toggleSelectAll();
};
</script>

<style lang="scss" scoped>
@reference "../../../../css/app.css";
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease-out;
    max-height: 1000px;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    max-height: 0;
}

.sub-details {
    @apply flex flex-col gap-2;
}

.list-enter-active,
.list-leave-active {
    transition: all 0.3s ease-in-out;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.list-enter-to,
.list-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.list-move {
    transition: transform 0.3s ease-in-out;
}
</style>
